import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import styled from 'styled-components';
import { FaUpload } from 'react-icons/fa';

const SettingsContainer = styled.div`
  padding: 0 5% 50px 5%;
  min-height: 100vh;
  background-color: #0b0614;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, var(--gradient-cyan), var(--gradient-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SettingsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const SettingsSection = styled.div`
    background: var(--glass-bg);
    border: 1px solid var(--border-color);
    border-radius: 12px;
    padding: 25px;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: var(--primary-color);
    border-bottom: 2px solid var(--gradient-pink);
    padding-bottom: 10px;
`;

const SettingItem = styled.div`
    margin-bottom: 20px;
    
    label {
        display: block;
        margin-bottom: 8px;
        font-weight: 600;
        color: var(--primary-color);
    }
    
    input, select, textarea {
        width: 100%;
        padding: 10px;
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid var(--border-color);
        border-radius: 8px;
        color: var(--primary-color);
        font-size: 1rem;
        
        &:focus {
            outline: none;
            border-color: var(--gradient-pink);
        }
    }
    
    textarea {
        min-height: 100px;
        resize: vertical;
    }
`;

const SaveButton = styled.button`
    padding: 12px 30px;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    background: var(--gradient-pink);
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(255, 0, 127, 0.3);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const LoadingText = styled.div`
    text-align: center;
    padding: 50px;
    color: var(--secondary-color);
    font-size: 1.2rem;
`;

const SuccessMessage = styled.div`
    background: rgba(46, 213, 115, 0.2);
    border: 1px solid #2ed573;
    color: #2ed573;
    padding: 15px;
    border-radius: 8px;
    margin-bottom: 20px;
`;

const ContentSearchContainer = styled.div`
    margin-bottom: 15px;
`;

const ContentSearchInput = styled.input`
    width: 100%;
    padding: 10px;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    color: var(--primary-color);
    font-size: 0.9rem;
    
    &:focus {
        outline: none;
        border-color: var(--gradient-pink);
    }
`;

const ContentList = styled.div`
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--border-color);
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.3);
`;

const ContentItem = styled.div`
    padding: 10px;
    border-bottom: 1px solid var(--border-color);
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
    
    &:last-child {
        border-bottom: none;
    }
    
    &.selected {
        background: var(--gradient-pink);
        color: white;
    }
`;

const SelectedContentList = styled.div`
    margin-top: 10px;
    max-height: 150px;
    overflow-y: auto;
`;

const SelectedContentItem = styled.div`
    padding: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
`;

const RemoveButton = styled.button`
    background: #ff4757;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.8rem;
    cursor: pointer;
    
    &:hover {
        background: #ff3742;
    }
`;

const IconPreview = styled.div`
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 15px;
    
    img {
        width: 48px;
        height: 48px;
        border-radius: 8px;
        border: 2px solid var(--border-color);
        object-fit: contain;
    }
`;

const FileUploadButton = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--gradient-cyan);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(0, 255, 255, 0.3);
    }
    
    input[type="file"] {
        display: none;
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const IconInputGroup = styled.div`
    display: flex;
    gap: 10px;
    align-items: flex-start;
    
    input {
        flex: 1;
    }
`;

const RadioGroup = styled.div`
    display: flex;
    gap: 15px;
    margin-bottom: 15px;
`;

const RadioLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
`;

const AdminSettingsPage = () => {
    const [settings, setSettings] = useState({
        logo_type: 'text',
        logo_initial: '',
        logo_text: '',
        logo_url: '',
        site_icon: '',
        hero_content_ids: [],
        featured_series_ids: [],
        featured_movies_ids: [],
        featured_animes_ids: [],
        featured_series_title: '',
        featured_movies_title: '',
        featured_animes_title: '',
        ad_home_top: '',
        ad_home_middle_1: '',
        ad_home_middle_2: '',
        ad_home_bottom: '',
        ad_watch_top: '',
        ad_watch_bottom: '',
        theme_primary_color: '#8a2be2',
        theme_secondary_color: '#ff007f',
        theme_gradient_purple: '#8a2be2',
        theme_gradient_pink: '#ff007f',
        theme_gradient_cyan: '#00f2ff'
    });
    const [iconUploading, setIconUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [allContent, setAllContent] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeSection, setActiveSection] = useState('');

    useEffect(() => {
        loadSettings();
        loadAllContent();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.GET_SETTINGS));
            if (response.ok) {
                const data = await response.json();
                const processedData = {
                    ...data,
                    hero_content_ids: data.hero_content_ids ? data.hero_content_ids.split(',').filter(id => id.trim() !== '') : [],
                    featured_series_ids: data.featured_series_ids ? data.featured_series_ids.split(',').filter(id => id.trim() !== '') : [],
                    featured_movies_ids: data.featured_movies_ids ? data.featured_movies_ids.split(',').filter(id => id.trim() !== '') : [],
                    featured_animes_ids: data.featured_animes_ids ? data.featured_animes_ids.split(',').filter(id => id.trim() !== '') : [],
                };
                setSettings(processedData);
            }
        } catch (error) {
            console.error('Ayarlar yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadAllContent = async () => {
        try {
            const response = await fetch(getApiUrl(API_ENDPOINTS.CHECK_CONTENT));
            if (response.ok) {
                const data = await response.json();
                setAllContent(data);
            }
        } catch (error) {
            console.error('İçerikler yüklenirken hata:', error);
        }
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const addContentToSection = (contentId, section) => {
        if (!settings[section].includes(contentId.toString())) {
            setSettings(prev => ({
                ...prev,
                [section]: [...prev[section], contentId.toString()]
            }));
        }
    };

    const removeContentFromSection = (contentId, section) => {
        setSettings(prev => ({
            ...prev,
            [section]: prev[section].filter(id => id !== contentId.toString())
        }));
    };

    const saveSettings = async () => {
        setLoading(true);
        setSaveStatus('');
        
        try {
            const settingsToSave = {
                ...settings,
                hero_content_ids: Array.isArray(settings.hero_content_ids) ? settings.hero_content_ids.join(',') : settings.hero_content_ids,
                featured_series_ids: Array.isArray(settings.featured_series_ids) ? settings.featured_series_ids.join(',') : settings.featured_series_ids,
                featured_movies_ids: Array.isArray(settings.featured_movies_ids) ? settings.featured_movies_ids.join(',') : settings.featured_movies_ids,
                featured_animes_ids: Array.isArray(settings.featured_animes_ids) ? settings.featured_animes_ids.join(',') : settings.featured_animes_ids,
            };

            const response = await fetch(getApiUrl(API_ENDPOINTS.SAVE_SETTINGS), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(settingsToSave)
            });

            if (response.ok) {
                setSaveStatus('success');
                setTimeout(() => setSaveStatus(''), 3000);
            } else {
                throw new Error('Kayıt başarısız');
            }
        } catch (error) {
            console.error('Ayarlar kaydedilirken hata:', error);
            setSaveStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const filteredContent = allContent.filter(content => 
        content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        content.tmdb_id.toString().includes(searchTerm)
    );

    const getContentById = (id) => {
        return allContent.find(content => content.tmdb_id.toString() === id.toString());
    };

    const fileInputRef = useRef(null);

    const handleIconUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Dosya tipi kontrolü
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Geçersiz dosya tipi. Sadece JPEG, PNG, GIF, ICO ve WebP dosyaları yüklenebilir.');
            return;
        }

        // Dosya boyutu kontrolü (2MB)
        if (file.size > 2 * 1024 * 1024) {
            alert('Dosya boyutu 2MB\'dan küçük olmalı');
            return;
        }

        setIconUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(getApiUrl(API_ENDPOINTS.UPLOAD_MEDIA), {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.url) {
                    handleSettingChange('site_icon', data.url);
                } else {
                    throw new Error(data.message || 'Yükleme başarısız');
                }
            } else {
                throw new Error('Yükleme başarısız');
            }
        } catch (error) {
            console.error('Icon yüklenirken hata:', error);
            alert('Icon yüklenirken hata oluştu: ' + error.message);
        } finally {
            setIconUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    if (loading && Object.keys(settings).length === 0) {
        return (
            <SettingsContainer>
                <Header>
                    <Title>Admin Paneli</Title>
                </Header>
                <LoadingText>Ayarlar yükleniyor...</LoadingText>
            </SettingsContainer>
        );
    }

    return (
        <SettingsContainer>
            <Header>
                <Title>Site Ayarları</Title>
            </Header>

            {saveStatus === 'success' && (
                <SuccessMessage>
                    ✅ Ayarlar başarıyla kaydedildi!
                </SuccessMessage>
            )}

            {saveStatus === 'error' && (
                <div style={{
                    background: 'rgba(255, 71, 87, 0.2)',
                    border: '1px solid #ff4757',
                    color: '#ff4757',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px'
                }}>
                    ❌ Ayarlar kaydedilirken hata oluştu!
                </div>
            )}

            <SettingsGrid>
                <SettingsSection>
                    <SectionTitle>Logo Ayarları</SectionTitle>
                    <RadioGroup>
                        <RadioLabel>
                            <input 
                                type="radio" 
                                name="logo_type" 
                                value="text" 
                                checked={settings.logo_type === 'text'} 
                                onChange={(e) => handleSettingChange('logo_type', e.target.value)}
                            />
                            Yazı
                        </RadioLabel>
                        <RadioLabel>
                            <input 
                                type="radio" 
                                name="logo_type" 
                                value="image" 
                                checked={settings.logo_type === 'image'} 
                                onChange={(e) => handleSettingChange('logo_type', e.target.value)}
                            />
                            Resim
                        </RadioLabel>
                    </RadioGroup>

                    {settings.logo_type === 'text' ? (
                        <>
                            <SettingItem>
                                <label>Logo Baş Harf</label>
                                <input
                                    type="text"
                                    maxLength="1"
                                    value={settings.logo_initial || ''}
                                    onChange={(e) => handleSettingChange('logo_initial', e.target.value)}
                                />
                            </SettingItem>
                            <SettingItem>
                                <label>Logo Metni</label>
                                <input
                                    type="text"
                                    value={settings.logo_text || ''}
                                    onChange={(e) => handleSettingChange('logo_text', e.target.value)}
                                />
                            </SettingItem>
                        </>
                    ) : (
                        <SettingItem>
                            <label>Logo Resim URL</label>
                            <input
                                type="text"
                                value={settings.logo_url || ''}
                                onChange={(e) => handleSettingChange('logo_url', e.target.value)}
                                placeholder="https://example.com/logo.png"
                            />
                        </SettingItem>
                    )}
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Site İkonu (Favicon)</SectionTitle>
                    <SettingItem>
                        <label>İkon URL</label>
                        <IconInputGroup>
                            <input
                                type="text"
                                value={settings.site_icon || ''}
                                onChange={(e) => handleSettingChange('site_icon', e.target.value)}
                                placeholder="https://example.com/favicon.ico veya yüklemek için dosya seçin"
                            />
                            <FileUploadButton as="label" disabled={iconUploading}>
                                <FaUpload />
                                {iconUploading ? 'Yükleniyor...' : 'Yükle'}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/jpg,image/png,image/gif,image/x-icon,image/vnd.microsoft.icon,image/webp"
                                    onChange={handleIconUpload}
                                    disabled={iconUploading}
                                />
                            </FileUploadButton>
                        </IconInputGroup>
                        <small style={{ color: 'var(--secondary-color)', marginTop: '5px', display: 'block' }}>
                            Site ikonu (favicon) tarayıcı sekmesinde görünecektir. ICO, PNG, JPEG veya WebP formatında olabilir.
                        </small>
                        {settings.site_icon && (
                            <IconPreview>
                                <img src={settings.site_icon} alt="Site İkonu Önizleme" onError={(e) => { e.target.style.display = 'none'; }} />
                                <span style={{ color: 'var(--secondary-color)', fontSize: '0.9rem' }}>
                                    Önizleme
                                </span>
                            </IconPreview>
                        )}
                    </SettingItem>
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Hero Slider İçerikleri</SectionTitle>
                    
                    <ContentSearchContainer>
                        <ContentSearchInput
                            type="text"
                            placeholder="İçerik ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setActiveSection('hero_content_ids')}
                        />
                    </ContentSearchContainer>

                    {activeSection === 'hero_content_ids' && (
                        <ContentList>
                            {filteredContent.slice(0, 10).map(content => (
                                <ContentItem
                                    key={content.tmdb_id}
                                    onClick={() => addContentToSection(content.tmdb_id, 'hero_content_ids')}
                                >
                                    <span>{content.title} ({content.media_type})</span>
                                    <span>ID: {content.tmdb_id}</span>
                                </ContentItem>
                            ))}
                        </ContentList>
                    )}

                    <SelectedContentList>
                        {settings.hero_content_ids.map(id => {
                            const content = getContentById(id);
                            return content ? (
                                <SelectedContentItem key={id}>
                                    <span>{content.title}</span>
                                    <RemoveButton onClick={() => removeContentFromSection(id, 'hero_content_ids')}>
                                        ✕
                                    </RemoveButton>
                                </SelectedContentItem>
                            ) : null;
                        })}
                    </SelectedContentList>
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Öne Çıkan Diziler</SectionTitle>
                    
                    <SettingItem>
                        <label>Başlık</label>
                        <input
                            type="text"
                            value={settings.featured_series_title || ''}
                            onChange={(e) => handleSettingChange('featured_series_title', e.target.value)}
                            placeholder="Popüler Diziler"
                        />
                    </SettingItem>
                    
                    <ContentSearchContainer>
                        <ContentSearchInput
                            type="text"
                            placeholder="Dizi ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setActiveSection('featured_series_ids')}
                        />
                    </ContentSearchContainer>

                    {activeSection === 'featured_series_ids' && (
                        <ContentList>
                            {filteredContent
                                .filter(content => content.media_type === 'tv')
                                .slice(0, 10)
                                .map(content => (
                                <ContentItem
                                    key={content.tmdb_id}
                                    onClick={() => addContentToSection(content.tmdb_id, 'featured_series_ids')}
                                >
                                    <span>{content.title}</span>
                                    <span>ID: {content.tmdb_id}</span>
                                </ContentItem>
                            ))}
                        </ContentList>
                    )}

                    <SelectedContentList>
                        {settings.featured_series_ids.map(id => {
                            const content = getContentById(id);
                            return content ? (
                                <SelectedContentItem key={id}>
                                    <span>{content.title}</span>
                                    <RemoveButton onClick={() => removeContentFromSection(id, 'featured_series_ids')}>
                                        ✕
                                    </RemoveButton>
                                </SelectedContentItem>
                            ) : null;
                        })}
                    </SelectedContentList>
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Öne Çıkan Filmler</SectionTitle>
                    
                    <SettingItem>
                        <label>Başlık</label>
                        <input
                            type="text"
                            value={settings.featured_movies_title || ''}
                            onChange={(e) => handleSettingChange('featured_movies_title', e.target.value)}
                            placeholder="Popüler Filmler"
                        />
                    </SettingItem>
                    
                    <ContentSearchContainer>
                        <ContentSearchInput
                            type="text"
                            placeholder="Film ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setActiveSection('featured_movies_ids')}
                        />
                    </ContentSearchContainer>

                    {activeSection === 'featured_movies_ids' && (
                        <ContentList>
                            {filteredContent
                                .filter(content => content.media_type === 'movie')
                                .slice(0, 10)
                                .map(content => (
                                <ContentItem
                                    key={content.tmdb_id}
                                    onClick={() => addContentToSection(content.tmdb_id, 'featured_movies_ids')}
                                >
                                    <span>{content.title}</span>
                                    <span>ID: {content.tmdb_id}</span>
                                </ContentItem>
                            ))}
                        </ContentList>
                    )}

                    <SelectedContentList>
                        {settings.featured_movies_ids.map(id => {
                            const content = getContentById(id);
                            return content ? (
                                <SelectedContentItem key={id}>
                                    <span>{content.title}</span>
                                    <RemoveButton onClick={() => removeContentFromSection(id, 'featured_movies_ids')}>
                                        ✕
                                    </RemoveButton>
                                </SelectedContentItem>
                            ) : null;
                        })}
                    </SelectedContentList>
                </SettingsSection>

                <SettingsSection>
                    <SectionTitle>Öne Çıkan Animeler</SectionTitle>
                    
                    <SettingItem>
                        <label>Başlık</label>
                        <input
                            type="text"
                            value={settings.featured_animes_title || ''}
                            onChange={(e) => handleSettingChange('featured_animes_title', e.target.value)}
                            placeholder="Popüler Animeler"
                        />
                    </SettingItem>
                    
                    <ContentSearchContainer>
                        <ContentSearchInput
                            type="text"
                            placeholder="Anime ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setActiveSection('featured_animes_ids')}
                        />
                    </ContentSearchContainer>

                    {activeSection === 'featured_animes_ids' && (
                        <ContentList>
                            {filteredContent
                                .filter(content => content.media_type === 'anime')
                                .slice(0, 10)
                                .map(content => (
                                <ContentItem
                                    key={content.tmdb_id}
                                    onClick={() => addContentToSection(content.tmdb_id, 'featured_animes_ids')}
                                >
                                    <span>{content.title}</span>
                                    <span>ID: {content.tmdb_id}</span>
                                </ContentItem>
                            ))}
                        </ContentList>
                    )}

                    <SelectedContentList>
                        {settings.featured_animes_ids.map(id => {
                            const content = getContentById(id);
                            return content ? (
                                <SelectedContentItem key={id}>
                                    <span>{content.title}</span>
                                    <RemoveButton onClick={() => removeContentFromSection(id, 'featured_animes_ids')}>
                                        ✕
                                    </RemoveButton>
                                </SelectedContentItem>
                            ) : null;
                        })}
                    </SelectedContentList>
                </SettingsSection>

                {/* Tema Renkleri */}
                <SettingsSection>
                    <SectionTitle>🎨 Tema Renkleri</SectionTitle>
                    
                    <SettingItem>
                        <label>Ana Renk (Primary - Mor)</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>

                            <input
                                type="color"
                                value={settings.theme_primary_color || '#8a2be2'}
                                onChange={(e) => {
                                    const color = e.target.value;
                                    handleSettingChange('theme_primary_color', color);
                                }}
                                style={{ 
                                    width: '80px', 
                                    height: '40px', 
                                    cursor: 'pointer',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px'
                                }}
                            />
                            <input
                                type="text"
                                value={settings.theme_primary_color || '#8a2be2'}
                                onChange={(e) => {
                                    let value = e.target.value.trim();
                                    // Hex formatını kontrol et
                                    if (value && !value.startsWith('#')) {
                                        value = '#' + value;
                                    }
                                    // Geçerli hex rengi kontrol et
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                                        handleSettingChange('theme_primary_color', value || '#8a2be2');
                                    }
                                }}
                                placeholder="#8a2be2"
                                style={{ flex: 1 }}
                                pattern="^#[0-9A-Fa-f]{6}$"
                            />
                        </div>
                        <small style={{ color: 'var(--secondary-color)', marginTop: '5px', display: 'block' }}>
                            Renk seçici ile seçin veya hex kodunu (#RRGGBB formatında) yazın
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>İkincil Renk (Secondary - Pembe)</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input
                                type="color"
                                value={settings.theme_secondary_color || '#ff007f'}
                                onChange={(e) => {
                                    const color = e.target.value;
                                    handleSettingChange('theme_secondary_color', color);
                                }}
                                style={{ 
                                    width: '80px', 
                                    height: '40px', 
                                    cursor: 'pointer',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px'
                                }}
                            />
                            <input
                                type="text"
                                value={settings.theme_secondary_color || '#ff007f'}
                                onChange={(e) => {
                                    let value = e.target.value.trim();
                                    if (value && !value.startsWith('#')) {
                                        value = '#' + value;
                                    }
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                                        handleSettingChange('theme_secondary_color', value || '#ff007f');
                                    }
                                }}
                                placeholder="#ff007f"
                                style={{ flex: 1 }}
                                pattern="^#[0-9A-Fa-f]{6}$"
                            />
                        </div>
                        <small style={{ color: 'var(--secondary-color)', marginTop: '5px', display: 'block' }}>
                            Renk seçici ile seçin veya hex kodunu (#RRGGBB formatında) yazın
                        </small>
                    </SettingItem>

                    <SettingItem>
                        <label>Gradient Mor</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input
                                type="color"
                                value={settings.theme_gradient_purple || '#8a2be2'}
                                onChange={(e) => {
                                    const color = e.target.value;
                                    handleSettingChange('theme_gradient_purple', color);
                                }}
                                style={{ 
                                    width: '80px', 
                                    height: '40px', 
                                    cursor: 'pointer',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px'
                                }}
                            />
                            <input
                                type="text"
                                value={settings.theme_gradient_purple || '#8a2be2'}
                                onChange={(e) => {
                                    let value = e.target.value.trim();
                                    if (value && !value.startsWith('#')) {
                                        value = '#' + value;
                                    }
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                                        handleSettingChange('theme_gradient_purple', value || '#8a2be2');
                                    }
                                }}
                                placeholder="#8a2be2"
                                style={{ flex: 1 }}
                                pattern="^#[0-9A-Fa-f]{6}$"
                            />
                        </div>
                    </SettingItem>

                    <SettingItem>
                        <label>Gradient Pembe</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input
                                type="color"
                                value={settings.theme_gradient_pink || '#ff007f'}
                                onChange={(e) => {
                                    const color = e.target.value;
                                    handleSettingChange('theme_gradient_pink', color);
                                }}
                                style={{ 
                                    width: '80px', 
                                    height: '40px', 
                                    cursor: 'pointer',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px'
                                }}
                            />
                            <input
                                type="text"
                                value={settings.theme_gradient_pink || '#ff007f'}
                                onChange={(e) => {
                                    let value = e.target.value.trim();
                                    if (value && !value.startsWith('#')) {
                                        value = '#' + value;
                                    }
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                                        handleSettingChange('theme_gradient_pink', value || '#ff007f');
                                    }
                                }}
                                placeholder="#ff007f"
                                style={{ flex: 1 }}
                                pattern="^#[0-9A-Fa-f]{6}$"
                            />
                        </div>
                    </SettingItem>

                    <SettingItem>
                        <label>Gradient Cyan</label>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input
                                type="color"
                                value={settings.theme_gradient_cyan || '#00f2ff'}
                                onChange={(e) => {
                                    const color = e.target.value;
                                    handleSettingChange('theme_gradient_cyan', color);
                                }}
                                style={{ 
                                    width: '80px', 
                                    height: '40px', 
                                    cursor: 'pointer',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '6px'
                                }}
                            />
                            <input
                                type="text"
                                value={settings.theme_gradient_cyan || '#00f2ff'}
                                onChange={(e) => {
                                    let value = e.target.value.trim();
                                    if (value && !value.startsWith('#')) {
                                        value = '#' + value;
                                    }
                                    if (/^#[0-9A-Fa-f]{6}$/.test(value) || value === '') {
                                        handleSettingChange('theme_gradient_cyan', value || '#00f2ff');
                                    }
                                }}
                                placeholder="#00f2ff"
                                style={{ flex: 1 }}
                                pattern="^#[0-9A-Fa-f]{6}$"
                            />
                        </div>
                    </SettingItem>

                    <div style={{ 
                        marginTop: '20px', 
                        padding: '15px', 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <div style={{ 
                            fontSize: '1.2rem', 
                            fontWeight: '600', 
                            marginBottom: '10px',
                            color: 'var(--primary-color)'
                        }}>
                            Önizleme
                        </div>
                        <div style={{ 
                            display: 'flex', 
                            gap: '15px', 
                            flexWrap: 'wrap',
                            alignItems: 'center'
                        }}>
                            <div style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                background: `linear-gradient(90deg, ${settings.theme_gradient_purple || '#8a2be2'}, ${settings.theme_gradient_pink || '#ff007f'})`,
                                color: 'white',
                                fontWeight: '600'
                            }}>
                                Gradient Örnek
                            </div>
                            <div style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                background: settings.theme_primary_color || '#8a2be2',
                                color: 'white',
                                fontWeight: '600'
                            }}>
                                Primary Renk
                            </div>
                            <div style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                background: settings.theme_secondary_color || '#ff007f',
                                color: 'white',
                                fontWeight: '600'
                            }}>
                                Secondary Renk
                            </div>
                        </div>
                        <small style={{ 
                            color: 'var(--secondary-color)', 
                            marginTop: '10px', 
                            display: 'block' 
                        }}>
                            Renkleri değiştirdikten sonra "Ayarları Kaydet" butonuna tıklayın. Değişiklikler sayfayı yeniledikten sonra görünecektir.
                        </small>
                    </div>
                </SettingsSection>

                {/* Reklam Yönetimi */}
                <SettingsSection>
                    <SectionTitle>📢 Reklam Yönetimi</SectionTitle>
                    
                    <SettingItem>
                        <label>Ana Sayfa - Üst Reklam (HTML/JavaScript Kodu)</label>
                        <textarea
                            value={settings.ad_home_top || ''}
                            onChange={(e) => handleSettingChange('ad_home_top', e.target.value)}
                            placeholder="HTML veya JavaScript reklam kodu yapıştırın..."
                            rows={4}
                        />
                        <small>Ana sayfanın en üstünde gösterilecek reklam</small>
                    </SettingItem>

                    <SettingItem>
                        <label>Ana Sayfa - Orta Reklam 1 (Sol) (HTML/JavaScript Kodu)</label>
                        <textarea
                            value={settings.ad_home_middle_1 || ''}
                            onChange={(e) => handleSettingChange('ad_home_middle_1', e.target.value)}
                            placeholder="HTML veya JavaScript reklam kodu yapıştırın..."
                            rows={4}
                        />
                        <small>Ana sayfada içerik slider'ları arasında sol tarafta gösterilecek reklam</small>
                    </SettingItem>

                    <SettingItem>
                        <label>Ana Sayfa - Orta Reklam 2 (Sağ) (HTML/JavaScript Kodu)</label>
                        <textarea
                            value={settings.ad_home_middle_2 || ''}
                            onChange={(e) => handleSettingChange('ad_home_middle_2', e.target.value)}
                            placeholder="HTML veya JavaScript reklam kodu yapıştırın..."
                            rows={4}
                        />
                        <small>Ana sayfada içerik slider'ları arasında sağ tarafta gösterilecek reklam (mobilde alt alta)</small>
                    </SettingItem>

                    <SettingItem>
                        <label>Ana Sayfa - Alt Reklam (HTML/JavaScript Kodu)</label>
                        <textarea
                            value={settings.ad_home_bottom || ''}
                            onChange={(e) => handleSettingChange('ad_home_bottom', e.target.value)}
                            placeholder="HTML veya JavaScript reklam kodu yapıştırın..."
                            rows={4}
                        />
                        <small>Ana sayfanın en altında gösterilecek reklam</small>
                    </SettingItem>

                    <SettingItem>
                        <label>İzleme Sayfası - Üst Reklam (HTML/JavaScript Kodu)</label>
                        <textarea
                            value={settings.ad_watch_top || ''}
                            onChange={(e) => handleSettingChange('ad_watch_top', e.target.value)}
                            placeholder="HTML veya JavaScript reklam kodu yapıştırın..."
                            rows={4}
                        />
                        <small>İzleme sayfasının üstünde gösterilecek reklam</small>
                    </SettingItem>

                    <SettingItem>
                        <label>İzleme Sayfası - Alt Reklam (HTML/JavaScript Kodu)</label>
                        <textarea
                            value={settings.ad_watch_bottom || ''}
                            onChange={(e) => handleSettingChange('ad_watch_bottom', e.target.value)}
                            placeholder="HTML veya JavaScript reklam kodu yapıştırın..."
                            rows={4}
                        />
                        <small>İzleme sayfasının altında (yorumların altında) gösterilecek reklam</small>
                    </SettingItem>
                </SettingsSection>

            </SettingsGrid>

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
                <SaveButton onClick={saveSettings} disabled={loading}>
                    {loading ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                </SaveButton>
            </div>
        </SettingsContainer>
    );
};

export default AdminSettingsPage;
